/*!
 * 使用者管理（v2.32.0）
 *
 * 只有 `manage_users`（Brad + Apex）進得來。前端這層只是不畫出來，
 * 真正的把關在後端每一支 API 的 @require_cap。
 *
 * 【初始密碼只出現一次】
 * 建立帳號 / 重設密碼的回應裡有明文密碼，那是唯一一次看得到它的機會
 * （DB 只存 bcrypt hash）。所以用 Modal 顯示、給複製鈕、並且把
 * 「關掉就查不回來」講在畫面上，而不是只寫在文件裡。
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Card, CardBody, CardHeader, CardTitle,
  CustomInput, FormGroup, Input, Label, Modal, ModalBody, ModalFooter,
  ModalHeader, Table,
} from "reactstrap";
import axios from "axios";

import {
  COLORS, cancelBtnStyle, inputStyle, labelStyle, modalBodyStyle,
  modalFooterStyle, modalHeaderStyle, mutedBadge, saveBtnStyle,
  softBadge, tdStyle, thStyle,
} from "constant/adminTheme";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyStatStrip from "components/tiny/TinyStatStrip";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
// 開關已抽成公版元件（原本這裡有一份 local Toggle，見 TinyToggle 的說明）
import Toggle from "components/tiny/TinyToggle";
/** 軟色調徽章。⚠️ 不要改回 reactstrap 的 <Badge color="...">，見 adminTheme.js 說明。 */
const Pill = ({ color, children, style }) => (
  <span style={color ? softBadge(color, style) : { ...mutedBadge, ...style }}>{children}</span>
);

/** 深色主題的開關。Bootstrap 的 custom-switch 開啟時在這個主題下很難看。 */

const CAP_LABELS = {
  view_revenue: "看得到營收數據",
  manage_users: "最高權限（使用者 + 稽核紀錄）",
  // v2.33.0：這個 cap 之前是 system_settings.cashflow_editors 一份獨立名單，
  // 沒有 UI、沒有稽核，一人一帳號上線後整個對不上（名單裡全是舊帳號）。
  edit_revenue: "可編輯帳務欄位（雜項費用 / 信用卡 / 佣金 / 交收）",
};

const fmtTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("zh-TW", { hour12: false });
};

export default function TinyUsers() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 新增帳號
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ username: "", display_name: "", caps: { view_revenue: true, edit_revenue: false, manage_users: false } });
  const [saving, setSaving] = useState(false);
  // v2.40.0：列上的停用 / 重設密碼 / 刪除原本只有 window.confirm，按下去之後沒有任何回饋、
  // 也沒有 disabled —— 慢一點就會被連按兩次。busy 記「哪一個帳號正在做什麼」。
  const [busy, setBusy] = useState(null); // `${action}:${username}` | null

  // 一次性密碼
  const [secret, setSecret] = useState(null); // { username, password, isReset }
  const [copied, setCopied] = useState(false);

  // 停用舊帳號

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/api/users");
      if (res.data.code === 1) {
        setUsers(res.data.data.users || []);
        setMe(res.data.data.me || "");
      } else {
        setError(res.data.msg || "載入失敗");
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  // ---- 動作 ----

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post("/api/users", form);
      if (res.data.code === 1) {
        setCreateOpen(false);
        setSecret({ username: form.username, password: res.data.data.initial_password, isReset: false });
        setForm({ username: "", display_name: "", caps: { view_revenue: true, edit_revenue: false, manage_users: false } });
        load();
      } else {
        setError(res.data.msg);
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCap = async (user, cap) => {
    const next = { ...user.caps, [cap]: !user.caps[cap] };
    setError(null);
    try {
      const res = await axios.put(`/api/users/${encodeURIComponent(user.username)}/caps`, { caps: next });
      if (res.data.code === 1) {
        setSuccess(`已更新 ${user.username} 的權限`);
        load();
      } else {
        setError(res.data.msg);
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
  };

  // v2.33.0：單一帳號的停用 / 啟用。
  // 先前只有「刪除」與「一鍵停用舊共用帳號」—— 結果那顆一鍵停用誤傷到
  // 會計帳號之後，UI 上完全救不回來，只能 SSH 進 VPS 改 DB。
  const handleToggleDisabled = async (u) => {
    const next = !u.disabled;
    if (next && !window.confirm(
      `停用 ${u.username}？\n\n對方會立刻無法登入，連目前還握在手上的 token 也會當場失效。\n（停用不是刪除，之後可以再啟用。）`
    )) return;
    if (busy) return;
    setBusy(`toggle:${u.username}`);
    setError(null);
    try {
      const res = await axios.patch(
        `/api/users/${encodeURIComponent(u.username)}/disabled`, { disabled: next });
      if (res.data.code === 1) {
        setSuccess(res.data.msg);
        load();
      } else {
        setError(res.data.msg);
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleReset = async (username) => {
    if (!window.confirm(`重設 ${username} 的密碼？\n\n對方目前的密碼會立刻失效，下次登入時會被要求重新設定。`)) return;
    if (busy) return;
    setBusy(`reset:${username}`);
    setError(null);
    try {
      const res = await axios.post(`/api/users/${encodeURIComponent(username)}/reset-password`);
      if (res.data.code === 1) {
        setSecret({ username, password: res.data.data.initial_password, isReset: true });
        load();
      } else {
        setError(res.data.msg);
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`刪除帳號 ${username}？\n\n這個動作無法復原。（稽核紀錄裡的歷史動作會保留。）`)) return;
    if (busy) return;
    setBusy(`delete:${username}`);
    setError(null);
    try {
      const res = await axios.delete(`/api/users/${encodeURIComponent(username)}`);
      if (res.data.code === 1) {
        setSuccess(res.data.msg);
        load();
      } else {
        setError(res.data.msg);
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setBusy(null);
    }
  };


  // ⚠️ `navigator.clipboard` 只在 secure context 下才有（https 或 localhost）。
  // VPS 走自簽 https 沒問題，但用 http://<內網 IP> 開後臺時它會是 undefined ——
  // 那正是「要複製密碼卻複製不了」最尷尬的時候，所以留一條 execCommand 的舊路。
  const copySecret = async () => {
    const text = secret?.password || "";
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        if (!ok) throw new Error("execCommand copy failed");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError("自動複製失敗，請手動選取上面那串密碼");
    }
  };

  // ---- 畫面 ----

  // ⚠️ v2.35.0 移除了「舊共用帳號退場」那張卡（Brad 2026-09-10：prod 與 dev
  //    都已停用完畢，dev 實查 4 筆 legacy_shared 全部 disabled）。
  //    但 `legacy_shared` 這個欄位**還要繼續讀**：
  //    ① 這裡用它把舊共用帳號排除在「尚未首次登入」的計數外
  //    ② 列表那一格仍掛「舊共用」標籤，說明那筆為什麼是停用的
  //    後端的 `POST /api/users/disable-legacy` 與 `list_legacy_accounts()`
  //    因此變成沒有前端在呼叫 —— 刻意留著沒刪，要不要清由 Brad 決定。
  const newAccounts = users.filter((u) => !u.legacy_shared);
  const pendingFirstLogin = newAccounts.filter((u) => u.must_change_password);

  return (
    <div className="content">
      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {success && <Alert color="success" toggle={() => setSuccess(null)}>{success}</Alert>}

      {/* ⚠️ platform:"system"，但側欄一樣會顯示 → 一樣套公版。
          🔴 這裡原本的順序是壞的：統計卡片在最上面，而 <TinyPageHeader> 被放在
             使用者表那張 Card 的**裡面**（Card 開了之後才出現）。
             正確順序是 header → summary → 主表。
          ⚠️ 五格統計原本一格一張 Card（lg=3 → 4+1 換行、兩排），
             Brad 2026-09-10：「縮小，反正不要換行」→ 換成公版的 TinyStatStrip。
          ⚠️「新增帳號」不再獨立佔一行，搬進使用者表的 CardHeader。 */}
      <TinyPageHeader
        title="使用者"
        description="一人一帳號。權限勾選後立即生效，不用等對方重新登入。"
      />

      <TinyStatStrip items={[
        { label: "帳號總數", value: users.length, color: "102, 126, 234", iconColor: COLORS.accent, icon: "fa-users" },
        { label: "看得到營收", value: users.filter((u) => u.caps?.view_revenue && !u.disabled).length, color: "17, 205, 239", iconColor: COLORS.info, icon: "fa-line-chart" },
        { label: "可編輯帳務", value: users.filter((u) => u.caps?.edit_revenue && !u.disabled).length, color: "45, 206, 137", iconColor: COLORS.success, icon: "fa-pen-to-square" },
        { label: "尚未首次登入", value: pendingFirstLogin.length, color: "251, 99, 64", iconColor: COLORS.warning, icon: "fa-hourglass-half" },
      ]} />

      {/* ---- 使用者表 ---- */}
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
            <CardTitle tag="h5" style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>
              <i className="fa fa-list mr-2" style={{ color: COLORS.accent }} />使用者列表
            </CardTitle>
            <Button size="sm" style={saveBtnStyle(false)} onClick={() => setCreateOpen(true)}>
              <i className="fa fa-user-plus mr-1" /> 新增帳號
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <TinyLoading text="載入使用者" variant="block" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table responsive style={{ color: COLORS.textSecondary }}>
                <thead>
                  <tr>
                    <th style={thStyle}>帳號</th>
                    <th style={thStyle}>顯示名稱</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>看得到營收</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>編輯帳務</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>最高權限</th>
                    <th style={thStyle}>狀態</th>
                    <th style={thStyle}>最後登入</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.username} style={u.disabled ? { opacity: 0.45 } : undefined}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.textPrimary }}>
                        {u.username}
                        {u.username === me && (
                          <Pill color={COLORS.info} style={{ marginLeft: 8 }}>你</Pill>
                        )}
                        {u.legacy_shared && (
                          <Pill color={COLORS.warning} style={{ marginLeft: 8 }}>舊共用</Pill>
                        )}
                      </td>
                      <td style={tdStyle}>{u.display_name}</td>
                      {[
                        { cap: "view_revenue", color: COLORS.info },
                        { cap: "edit_revenue", color: COLORS.success },
                        { cap: "manage_users", color: COLORS.accent },
                      ].map(({ cap, color }) => (
                        <td style={{ ...tdStyle, textAlign: "center" }} key={cap}>
                          <Toggle
                            on={Boolean(u.caps?.[cap])}
                            color={color}
                            disabled={u.disabled}
                            onClick={() => toggleCap(u, cap)}
                            title={`${u.username}：${CAP_LABELS[cap]}`}
                          />
                        </td>
                      ))}
                      <td style={tdStyle}>
                        {u.disabled ? <Pill>已停用</Pill>
                          : u.must_change_password
                            ? <Pill color={COLORS.warning}>待首次登入</Pill>
                            : <Pill color={COLORS.success}>正常</Pill>}
                      </td>
                      <td style={{ ...tdStyle, color: COLORS.textMuted, fontSize: 12 }}>{fmtTime(u.last_login_at)}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <Button size="sm" color="link"
                          style={{ color: u.disabled ? COLORS.success : COLORS.warning }}
                          onClick={() => handleToggleDisabled(u)}
                          disabled={u.username === me || !!busy}
                          title={u.username === me ? "不能停用自己" : (u.disabled ? "重新啟用" : "停用")}>
                          {busy === `toggle:${u.username}` ? <TinyBusy /> : <i className={`fa fa-${u.disabled ? "play-circle" : "ban"}`} />}
                          {" "}{u.disabled ? "啟用" : "停用"}
                        </Button>
                        <Button size="sm" color="link" style={{ color: COLORS.info }}
                          onClick={() => handleReset(u.username)} title="重設密碼" disabled={!!busy}>
                          {busy === `reset:${u.username}` ? <TinyBusy /> : <i className="fa fa-refresh" />} 重設密碼
                        </Button>
                        <Button size="sm" color="link" style={{ color: COLORS.danger }}
                          onClick={() => handleDelete(u.username)}
                          disabled={u.username === me || !!busy} title={u.username === me ? "不能刪除自己" : "刪除"}>
                          {busy === `delete:${u.username}` ? <TinyBusy /> : <i className="fa fa-trash" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!users.length && (
                    <tr><td colSpan="8" style={{ ...tdStyle, textAlign: "center", color: COLORS.textMuted }}>沒有任何帳號</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- 新增帳號 Modal ---- */}
      <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)}>
        <ModalHeader style={modalHeaderStyle} toggle={() => setCreateOpen(false)}>
          <i className="fa fa-user-plus mr-2" style={{ color: COLORS.accent }} />新增帳號
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          <FormGroup>
            <Label style={labelStyle}>帳號 <span style={{ color: COLORS.danger }}>*</span></Label>
            <Input style={inputStyle} value={form.username} autoComplete="off"
              placeholder="英數與 . _ -，2~32 字"
              onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label style={labelStyle}>顯示名稱</Label>
            <Input style={inputStyle} value={form.display_name}
              placeholder="留白就用帳號"
              onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label style={labelStyle}>權限</Label>
            {Object.entries(CAP_LABELS).map(([cap, label]) => (
              <CustomInput key={cap} type="checkbox" id={`new-${cap}`} label={label}
                checked={Boolean(form.caps[cap])}
                onChange={(e) => setForm({ ...form, caps: { ...form.caps, [cap]: e.target.checked } })} />
            ))}
          </FormGroup>
          <small style={{ color: COLORS.textMuted }}>
            <i className="fa fa-info-circle mr-1" />
            系統會產生一組隨機初始密碼，<strong>只顯示這一次</strong>；對方首次登入時會被要求自行修改。
          </small>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button style={cancelBtnStyle} onClick={() => setCreateOpen(false)}>取消</Button>
          <Button style={saveBtnStyle(!form.username || saving)}
            disabled={!form.username || saving} onClick={handleCreate}>
            {saving ? "建立中..." : "建立"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---- 一次性密碼 Modal ---- */}
      <Modal isOpen={Boolean(secret)} toggle={() => setSecret(null)} backdrop="static">
        <ModalHeader style={modalHeaderStyle}>
          <i className="fa fa-key mr-2" style={{ color: COLORS.warning }} />
          {secret?.isReset ? "密碼已重設" : "帳號已建立"}
        </ModalHeader>
        <ModalBody style={modalBodyStyle}>
          <p style={{ color: COLORS.textSecondary }}>
            <code style={{ color: COLORS.accent, fontSize: 15 }}>{secret?.username}</code> 的初始密碼：
          </p>
          <div style={{
            background: COLORS.inputBg, border: `1px dashed ${COLORS.borderInput}`,
            borderRadius: 6, padding: "14px 16px", display: "flex",
            alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <code style={{ color: COLORS.success, fontSize: 18, letterSpacing: 1, wordBreak: "break-all" }}>
              {secret?.password}
            </code>
            <Button size="sm" style={saveBtnStyle(false)} onClick={copySecret}>
              <i className={`fa ${copied ? "fa-check" : "fa-clipboard"} mr-1`} />{copied ? "已複製" : "複製"}
            </Button>
          </div>
          <Alert color="warning" className="mt-3 mb-0">
            <i className="fa fa-exclamation-triangle mr-2" />
            <strong>這串密碼只會出現這一次。</strong>關掉這個視窗之後系統也查不回來（資料庫只存加密後的雜湊），
            忘記了只能重設。建議用私訊傳給對方，不要貼在群組。
          </Alert>
        </ModalBody>
        <ModalFooter style={modalFooterStyle}>
          <Button style={saveBtnStyle(false)} onClick={() => setSecret(null)}>我已經記下來了</Button>
        </ModalFooter>
      </Modal>

      {/* ---- 停用舊帳號確認 ---- */}
    </div>
  );
}
